<template>
  <layout-default :floatingButton="false">
    <template v-slot:toolbar>
      <smart-exercises-filters
        :query-params="queryParams"
        @search-clear="searchClear"
        @search-on:input="searchInput"
        @search-on:run="searchRun"
      />
    </template>
    <div class="exercises">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div class="exercises__wrapper" v-if="dialogExerciseDefinitions">
        <exercises-activities
          :exercise-definitions="dialogExerciseDefinitions"
          :query-params="queryParams"
          :get-resource-by-id="getResourceById"
          :progress-bars-values="progressBarsValues"
          :advanced-search-active="advancedExerciseSearchActive"
          :exercise-folders="routineDefinitionFoldersSharedList"
          :mass-select="true"
          :movable="true"
          :collection-mode="true"
          :readonly="true"
          :has-imports-folders="true"
          :pagination="dialogPagination"
          @search-on:input="searchInput"
          @search-on:run="searchRun"
          @import="multipleItemsSharedDefinitionsImport"
          @import-folder="importFolder"
          @open-hover="exerciseOpenHover"
          @close-hover="exerciseCloseHover"
          @clear-hover="exerciseClearHover"
          @get-exercise-preview="exerciseGetPreview"
          @pagination-change="paginationChange"
          @selected-exercises="selectedExercises"
          v-on="$listeners"
        />
      </div>
      <h4 v-else>nothing found</h4>
      <v-btn
        v-if="this.smartActivitiesItemsSelected.items.length > 0"
        elevation="0"
        color="accent"
        rounded
        class="exercises__btn"
        @click="multipleItemsSharedDefinitionsImport"
        >Import</v-btn
      >
      <preview-exercise-dialog
        :show-dialog="showExercisePreviewDialog"
        @close-dialog="closeDialogs"
        @preview-media="openMediaPreview"
        :exercise="exerciseDefinition"
      />
    </div>
  </layout-default>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import ExercisesActivities from '@/components/exercises/ExercisesActivities.vue'
import PreviewExerciseDialog from '@/components/dialogs/PreviewExerciseDialog.vue'
import SmartExercisesFilters from '@/components/exercises/SmartExercisesFilters.vue'
import RouterMixin from '@/mixins/router'
import { debounce } from '@/helpers/debounce'

export default {
  name: 'SmartExercises',
  components: {
    LayoutDefault,
    ExercisesActivities,
    PreviewExerciseDialog,
    SmartExercisesFilters,
  },
  data() {
    return {
      defaultQueryParams: {
        pageSize: 50,
      },
      exercisePreviewDialog: false,
      progressBarsValues: {},
      advancedExerciseSearchActive: false,
      showExercisePreviewDialog: false,
      smartActivitiesItemsSelected: {
        items: [],
      },
    }
  },
  mounted() {
    this.$router.push({
      query: {
        filter: 'all',
      },
    })
  },
  mixins: [RouterMixin],
  computed: {
    ...mapState('exercisesStates', [
      'dialogExerciseDefinitions',
      'exerciseDefinition',
      'loading',
      'dialogPagination',
      'routineDefinitionFoldersSharedList',
    ]),
  },
  methods: {
    ...mapActions('exercisesStates', [
      'getSharedExerciseDefinitions',
      'shareExerciseDefinition',
      'getRoutineDefinitionFoldersSharedList',
      'shareFolderDefinition',
      'copyMultipleItemsSharedDefinitions',
    ]),
    ...mapActions('resourcesStates', ['getResource', 'getResourceById']),
    searchClear() {
      this.getDataAction({ ...this.defaultQueryParams })
    },
    searchInput({ key, value }) {
      this.getDataAction({ ...this.defaultQueryParams, [key]: value })
      this.getDataAction({ ...this.queryParams, [key]: value })
    },
    searchRun(search) {
      this.getDataAction({ ...this.defaultQueryParams, title: search })
    },
    getData(data) {
      debounce(
        () => {
          this.getSharedExerciseDefinitions(data)
          this.getRoutineDefinitionFoldersSharedList(data)
        },
        100,
        'getData',
      )
    },
    importFolder(folderId) {
      this.shareFolderDefinition({ folderId: folderId })
    },
    exerciseOpenHover(
      id,
      media = false,
      mobileStaticMedia = null,
      additionalMedia = null,
    ) {
      clearTimeout(this.exerciseHoverTimeout)
      this.exerciseCloseHover()
      if (
        id &&
        (media?.thumbnailUrl ||
          media?.previewUrl ||
          mobileStaticMedia?.mediaUrl)
      ) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.exerciseHoverInterval)
          clearInterval(this.exerciseHoverInterval)
        this.exerciseHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.exerciseHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.exerciseHoverTimeout) clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHoverTimeout = setTimeout(() => {
          this.exerciseHover = null
          if (
            media?.previewUrl ||
            additionalMedia?.previewUrl ||
            mobileStaticMedia?.mediaUrl
          ) {
            this.exerciseHoverUrl =
              media?.previewUrl ||
              additionalMedia?.previewUrl ||
              mobileStaticMedia?.mediaUrl
            this.exerciseHoverType = this.isStringBase64(
              media?.previewUrl ||
                additionalMedia?.previewUrl ||
                mobileStaticMedia?.mediaUrl,
            )
              ? 'base64'
              : 'url'
            this.exerciseHover = true
          }
        }, 1000)
      } else this.exerciseClearHover(id)
    },
    exerciseClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.exerciseHoverInterval)
      if (!this.exerciseHover) {
        clearTimeout(this.exerciseHoverTimeout)
        this.exerciseHover = false
        this.exerciseHoverUrl = null
        this.exerciseHoverType = 'base64'
      }
    },
    exerciseCloseHover() {
      this.exerciseHover = false
    },
    async exerciseGetPreview(exercise) {
      await this.getExerciseDefinition({ id: exercise.id, type: exercise.type })
      this.showExercisePreviewDialog = true
    },
    closeDialogs() {
      this.showExercisePreviewDialog = false
    },
    openMediaPreview(media) {
      this.showBiggerImage({
        url: media.mediaUrl,
        file: media.mediaUrl,
        name: media.filename,
        mlResourceUrl: null,
        type: media.fileTypeId == 19 ? 3 : 1,
      })
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.dialogFile = file
      this.imageName = name
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    paginationChange(input) {
      this.getDataAction({ ...this.defaultQueryParams, page: input })
    },
    selectedExercises(selectedItems) {
      const items = selectedItems.map(item => Number(item.id))
      this.smartActivitiesItemsSelected = { items: items }
    },
    multipleItemsSharedDefinitionsImport({ exerciseId }) {
      const items = { items: [exerciseId] }
      if (exerciseId) this.copyMultipleItemsSharedDefinitions(items)
      else
        this.copyMultipleItemsSharedDefinitions({
          ...this.smartActivitiesItemsSelected,
        })
      this.smartActivitiesItemsSelected = {
        items: [],
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
.exercises {
  padding: 2 * $gutter;
  height: 100%;
  display: flex;
  flex-direction: column;
  &__wrapper {
    height: 100%;
  }
  &__btn {
    max-width: max-content;
    align-self: flex-end;
  }
  &-activities {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  &-exercises {
    height: 100%;
    display: flex;
    &__wrapper {
      display: flex;
      flex-wrap: wrap;
      margin-left: -$gutter/2;
      margin-right: -$gutter/2;
      flex-grow: 1;
      align-content: flex-start;
    }
  }
  .v-pagination {
    &__item,
    &__navigation {
      box-shadow: none;
    }
  }
}
</style>
